/**
 * Team Configuration Service
 * 
 * Frontend service for accessing team configuration from the backend API.
 * This replaces hardcoded team lists with dynamic API-driven configuration.
 */

import { apiClient } from './apiService'

// TypeScript interfaces for team configuration
export interface Team {
  name: string
  displayName: string
  status: 'active' | 'inactive' | 'deprecated'
  description?: string
  category?: string
  aliases?: string[]
  sprintPatterns?: string[]
  metadata?: {
    jiraProject?: string
    slackChannel?: string
    teamLead?: string
    [key: string]: any
  }
}

export interface TeamConfigMetadata {
  version: string
  lastUpdated: string
  updatedBy: string
  source: string
  description: string
  totalTeams: number
  activeTeams: number
  inactiveTeams: number
  deprecatedTeams: number
  specialMappings: number
}

export interface SpecialMapping {
  pattern: string
  targetTeam: string
  confidence: number
  description: string
}

export interface ValidationError {
  field: string
  code: string
  message: string
  teamIndex?: number
  teamName?: string
}

export interface ValidationWarning {
  field: string
  code: string
  message: string
  suggestion?: string
  teamIndex?: number
  teamName?: string
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
  warnings: ValidationWarning[]
  summary?: {
    totalTeams: number
    validTeams: number
    uniqueNames: number
    totalErrors: number
    totalWarnings: number
  }
}

export interface HealthCheck {
  status: 'healthy' | 'unhealthy'
  checks: {
    configurationLoaded: boolean
    teamsAvailable: boolean
    noDuplicateNames: boolean
    activeTeamsAvailable: boolean
    validationRulesPresent: boolean
    noValidationErrors: boolean
    specialMappingsValid: boolean
  }
  statistics: {
    totalTeams: number
    activeTeams: number
    validationErrors: number
    validationWarnings: number
    configVersion: string
    lastUpdated: string | null
  }
  validation?: ValidationResult
  failedChecks?: string[]
}

// API Response interfaces
export interface TeamNamesResponse {
  success: boolean
  data: string[]
  metadata: {
    count: number
    version: string
    lastUpdated: string
  }
  message: string
  error?: string
}

export interface TeamDetailsResponse {
  success: boolean
  data: Team[]
  metadata: TeamConfigMetadata & {
    filteredTeams: number
    filter: { status: string }
  }
  message: string
  error?: string
}

export interface TeamMetadataResponse {
  success: boolean
  data: TeamConfigMetadata
  message: string
  error?: string
}

export interface TeamResponse {
  success: boolean
  data: Team
  message: string
  error?: string
}

export interface ValidationResponse {
  success: boolean
  data: ValidationResult
  message: string
  error?: string
}

export interface SpecialMappingsResponse {
  success: boolean
  data: SpecialMapping[]
  message: string
  error?: string
}

export interface HealthResponse {
  success: boolean
  data: HealthCheck
  message: string
  error?: string
}

/**
 * Team Configuration Service Class
 * 
 * Provides methods to interact with the backend team configuration API.
 * Includes error handling, caching, and fallback mechanisms.
 */
class TeamConfigService {
  private baseUrl = '/teams'

  /**
   * Get list of active team names
   * This is the primary method for getting team names for filters and dropdowns
   */
  async getTeamNames(): Promise<TeamNamesResponse> {
    try {
      const response = await apiClient.get<TeamNamesResponse>(this.baseUrl)
      return response.data
    } catch (error) {
      console.error('Error fetching team names:', error)
      return {
        success: false,
        data: [],
        metadata: {
          count: 0,
          version: 'unknown',
          lastUpdated: new Date().toISOString()
        },
        message: 'Failed to fetch team names',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get detailed team configuration
   * @param status - Filter teams by status ('active', 'inactive', 'deprecated', 'all')
   */
  async getTeamDetails(status: string = 'active'): Promise<TeamDetailsResponse> {
    try {
      const params = status !== 'active' ? { status } : {}
      const response = await apiClient.get<TeamDetailsResponse>(`${this.baseUrl}/details`, { params })
      return response.data
    } catch (error) {
      console.error('Error fetching team details:', error)
      return {
        success: false,
        data: [],
        metadata: {
          version: 'unknown',
          lastUpdated: new Date().toISOString(),
          updatedBy: 'unknown',
          source: 'error',
          description: 'Error response',
          totalTeams: 0,
          activeTeams: 0,
          inactiveTeams: 0,
          deprecatedTeams: 0,
          specialMappings: 0,
          filteredTeams: 0,
          filter: { status }
        },
        message: 'Failed to fetch team details',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get team configuration metadata and statistics
   */
  async getTeamMetadata(): Promise<TeamMetadataResponse> {
    try {
      const response = await apiClient.get<TeamMetadataResponse>(`${this.baseUrl}/metadata`)
      return response.data
    } catch (error) {
      console.error('Error fetching team metadata:', error)
      return {
        success: false,
        data: {
          version: 'unknown',
          lastUpdated: new Date().toISOString(),
          updatedBy: 'unknown',
          source: 'error',
          description: 'Error response',
          totalTeams: 0,
          activeTeams: 0,
          inactiveTeams: 0,
          deprecatedTeams: 0,
          specialMappings: 0
        },
        message: 'Failed to fetch team metadata',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get specific team by name
   * @param teamName - Name of the team to retrieve
   */
  async getTeam(teamName: string): Promise<TeamResponse> {
    try {
      const response = await apiClient.get<TeamResponse>(`${this.baseUrl}/${encodeURIComponent(teamName)}`)
      return response.data
    } catch (error) {
      console.error(`Error fetching team '${teamName}':`, error)
      return {
        success: false,
        data: {
          name: teamName,
          displayName: teamName,
          status: 'active'
        },
        message: `Failed to fetch team '${teamName}'`,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Validate team configuration
   * @param teams - Array of team objects to validate
   */
  async validateTeams(teams: Partial<Team>[]): Promise<ValidationResponse> {
    try {
      const response = await apiClient.post<ValidationResponse>(`${this.baseUrl}/validate`, { teams })
      return response.data
    } catch (error) {
      console.error('Error validating teams:', error)
      return {
        success: false,
        data: {
          valid: false,
          errors: [{
            field: 'request',
            code: 'VALIDATION_ERROR',
            message: 'Failed to validate team configuration'
          }],
          warnings: []
        },
        message: 'Failed to validate team configuration',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get special mapping rules
   */
  async getSpecialMappings(): Promise<SpecialMappingsResponse> {
    try {
      const response = await apiClient.get<SpecialMappingsResponse>(`${this.baseUrl}/special-mappings`)
      return response.data
    } catch (error) {
      console.error('Error fetching special mappings:', error)
      return {
        success: false,
        data: [],
        message: 'Failed to fetch special mappings',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get team configuration health status
   */
  async getHealthStatus(): Promise<HealthResponse> {
    try {
      const response = await apiClient.get<HealthResponse>(`${this.baseUrl}/health`)
      return response.data
    } catch (error) {
      console.error('Error fetching health status:', error)
      return {
        success: false,
        data: {
          status: 'unhealthy',
          checks: {
            configurationLoaded: false,
            teamsAvailable: false,
            noDuplicateNames: false,
            activeTeamsAvailable: false,
            validationRulesPresent: false,
            noValidationErrors: false,
            specialMappingsValid: false
          },
          statistics: {
            totalTeams: 0,
            activeTeams: 0,
            validationErrors: 1,
            validationWarnings: 0,
            configVersion: 'unknown',
            lastUpdated: null
          },
          failedChecks: ['configurationLoaded', 'teamsAvailable']
        },
        message: 'Failed to fetch health status',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

// Export singleton instance
export const teamConfigService = new TeamConfigService()
export default teamConfigService
